import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import { CameraAlt, FlipCameraIos, Close } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { CardSurface } from '../../ui/CardSurface';
import { IconButtonRounded } from '../../ui/IconButtonRounded';
import { ButtonPrimary } from '../../ui/ButtonPrimary';
import { PermissionRequestScreen } from './PermissionRequestScreen';
import { checkCameraPermission } from '../utils/permissions';
import { compressImageFromCanvas } from '../utils/imageCompression';

export function CameraViewer({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [error, setError] = useState(null);
  const [solicitandoPermissao, setSolicitandoPermissao] = useState(true);
  const [checkingPermission, setCheckingPermission] = useState(true);

  useEffect(() => {
    // Verificar se a permissão já foi concedida
    checkCameraPermission().then((hasPermission) => {
      if (hasPermission) {
        console.log('[CameraViewer] Permissão de câmera já concedida, iniciando câmera diretamente...');
        setSolicitandoPermissao(false);
      } else {
        console.log('[CameraViewer] Permissão de câmera não concedida, mostrando tela de permissão...');
        setSolicitandoPermissao(true);
      }
      setCheckingPermission(false);
    }).catch((error) => {
      console.error('[CameraViewer] Erro ao verificar permissão de câmera:', error);
      // Em caso de erro, mostrar tela de permissão por segurança
      setSolicitandoPermissao(true);
      setCheckingPermission(false);
    });
    
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    // Iniciar câmera quando permissão for concedida
    if (!solicitandoPermissao) {
      startCamera();
    }

    return () => {
      if (!solicitandoPermissao) {
        stopCamera();
      }
    };
  }, [facingMode, solicitandoPermissao]);

  const startCamera = async () => {
    try {
      stopCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setError(null);
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      setError('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const switchCamera = async () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const capture = async () => {
    if (!videoRef.current) return;

    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Comprimir imagem antes de retornar
      // Redimensionar para máximo 1920x1080 e comprimir para máximo 500KB
      console.log('[CameraViewer] Comprimindo imagem capturada...');
      const compressedBlob = await compressImageFromCanvas(
        canvas,
        1920,  // maxWidth
        1080,  // maxHeight
        0.85,  // qualidade inicial
        500    // maxSizeKB
      );

      stopCamera();
      if (compressedBlob && onCapture) {
        console.log(`[CameraViewer] Imagem comprimida com sucesso: ${(compressedBlob.size / 1024).toFixed(2)}KB`);
        onCapture(compressedBlob);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('[CameraViewer] Erro ao comprimir imagem:', error);
      // Em caso de erro na compressão, tentar capturar sem compressão
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        stopCamera();
        if (blob && onCapture) {
          console.warn('[CameraViewer] Usando imagem sem compressão devido a erro');
          onCapture(blob);
        } else {
          onClose();
        }
      }, 'image/jpeg', 0.85);
    }
  };

  // Mostrar loading enquanto verifica permissão
  if (checkingPermission) {
    return null; // Ou um loading spinner se preferir
  }

  // Mostrar tela de permissão antes de solicitar câmera
  if (solicitandoPermissao) {
    return (
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            m: 0,
            borderRadius: 0,
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <PermissionRequestScreen
            type="camera"
            onRequest={() => {
              setSolicitandoPermissao(false);
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
          },
        }}
      >
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Erro ao acessar câmera
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <ButtonPrimary onClick={onClose} size="large">
              Fechar
            </ButtonPrimary>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'black',
          m: 0,
          borderRadius: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Botão de trocar câmera no canto superior direito */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <IconButtonRounded
              onClick={switchCamera}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                '&:hover': { 
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  transform: 'scale(1.05)',
                },
              }}
              size="large"
            >
              <FlipCameraIos />
            </IconButtonRounded>
          </Box>

          <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            
            {/* Overlay com máscara vertical - totalmente transparente */}
            <Box
              sx={{
                position: 'absolute',
                top: '12%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60%',
                height: '70%',
                border: '3px solid transparent',
                borderRadius: '14px',
                pointerEvents: 'none',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
              }}
            />
          </Box>

          {/* Base com transparência e altura menor */}
          <CardSurface
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              borderRadius: '20px 20px 0 0',
              bgcolor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {/* Botão Cancelar */}
              <Button
                variant="outlined"
                onClick={onClose}
                size="large"
                startIcon={<Close />}
                sx={{
                  flex: 1,
                  py: 1.25,
                  borderRadius: '14px',
                  borderWidth: 2,
                  fontWeight: 600,
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50',
                  },
                }}
              >
                Cancelar
              </Button>
              
              {/* Botão Capturar */}
              <ButtonPrimary
                startIcon={<CameraAlt />}
                onClick={capture}
                size="large"
                sx={{
                  flex: 1,
                  py: 1.25,
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                Capturar
              </ButtonPrimary>
            </Box>
          </CardSurface>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
